import { CONSIGNES, consigneKeyFromPayload } from '../consignes';
import { estTableau, tableConvertible, reponseTable, estColonnesVraiFaux } from './reponseLibre';

/**
 * Politique « feuille / cahier » par format d'exercice.
 *
 * Le prof choisit un mode GLOBAL dans LaunchModal (meta.saisieMode) :
 *   - 'sur_la_feuille' → l'élève répond sur la feuille imprimée (cases,
 *     lignes pointillées, tableaux à compléter, traits à tracer) ;
 *   - 'sur_le_cahier'  → la feuille ne porte que les énoncés, l'élève
 *     répond sur son cahier (consignes adaptées, zones de réponse masquées).
 *
 * Certains formats n'ont AUCUN sens sur cahier (placer des points sur la
 * droite graduée imprimée, construction au compas dans la zone de travail) :
 * ils restent « sur la feuille » quel que soit le choix global
 * (mode 'feuille_forcee' — les renderers affichent une petite mention).
 */

const SUBTYPES_FEUILLE_FORCEE = new Set(['placer_points', 'programme_construction', 'placer_aiguilles']);

/**
 * Mode effectif d'un exercice : 'feuille' | 'cahier' | 'feuille_forcee'.
 */
export const modeEffectif = (payload, saisieMode) => {
    if (saisieMode !== 'sur_le_cahier') return 'feuille';
    if (payload?.type === 'association' && SUBTYPES_FEUILLE_FORCEE.has(payload?.contenu?.subType)) {
        return 'feuille_forcee';
    }
    return 'cahier';
};

/** Consignes « réponds sur ton cahier » — mêmes clés que CONSIGNES. */
export const CONSIGNES_CAHIER = {
    qcm_simple: 'Écris sur ton cahier la lettre de la bonne réponse.',
    qcm_multi: 'Écris sur ton cahier la ou les lettres des bonnes réponses.',
    vf: 'Pour chaque affirmation, écris Vrai ou Faux sur ton cahier.',
    table: 'Recopie et complète le tableau sur ton cahier.',
    relier: 'Sur ton cahier, associe chaque numéro à la bonne lettre (ex. 1 → C).',
    relier_leurres: 'Sur ton cahier, associe chaque numéro à la bonne lettre (ex. 1 → C). Attention : certaines lettres ne servent pas.',
    ordonner: 'Écris les propositions dans le bon ordre sur ton cahier.',
    classer: 'Sur ton cahier, classe chaque élément dans la bonne catégorie.',
    texte_trou_drag: 'Recopie sur ton cahier avec les mots manquants.',
    texte_trou_saisie: 'Recopie sur ton cahier avec les valeurs manquantes.',
    saisie: 'Réponds sur ton cahier.',
};

/** « A, B ou C » */
const joindreLibelles = (libelles) => {
    const l = libelles.map((x) => String(x ?? '').trim()).filter(Boolean);
    if (l.length <= 1) return l.join('');
    return `${l.slice(0, -1).join(', ')} ou ${l[l.length - 1]}`;
};

/**
 * Consigne d'un tableau rendu en LISTE (réponse libre sur la feuille, ou
 * mode cahier). Fondée sur les LIBELLÉS de colonnes (`config.type` ne
 * garantit pas des colonnes Vrai/Faux) : un vrai V/F demande une
 * justification quand `justifier` (réglage Libres) ; un tableau à colonnes
 * nommées liste les réponses possibles.
 */
export const consigneTableLibre = (colonnes, enCahier = false, { justifier = true } = {}) => {
    if (estColonnesVraiFaux(colonnes)) {
        if (enCahier) {
            return justifier
                ? 'Pour chaque affirmation, écris Vrai ou Faux sur ton cahier et justifie chaque réponse.'
                : 'Pour chaque affirmation, écris Vrai ou Faux sur ton cahier.';
        }
        return justifier
            ? 'Pour chaque affirmation, écris Vrai ou Faux. Justifie chaque réponse.'
            : 'Pour chaque affirmation, écris Vrai ou Faux.';
    }
    const liste = joindreLibelles(colonnes);
    return enCahier
        ? `Sur ton cahier, écris pour chaque ligne la bonne réponse : ${liste}.`
        : `Pour chaque ligne, écris la bonne réponse : ${liste}.`;
};

/** Un tableau est rendu en liste : réponse libre (feuille) ou mode cahier. */
export const tableEnListe = (payload, saisieMode, libre = false) =>
    tableConvertible(payload) && (libre || modeEffectif(payload, saisieMode) === 'cahier');

/**
 * Consigne adaptée au mode effectif de l'exercice.
 * `libre` : exercice converti en réponse libre (aperçu) → consigne de saisie
 * pour un QCM, consigne dédiée pour un tableau (également en mode cahier,
 * où le tableau devient une liste : la consigne doit nommer les réponses
 * possibles, « écris Vrai ou Faux » étant faux pour des colonnes < = >).
 */
export const consignePour = (payload, saisieMode, { libre = false } = {}) => {
    if (estTableau(payload) && tableEnListe(payload, saisieMode, libre)) {
        return consigneTableLibre(
            reponseTable(payload).colonnes,
            modeEffectif(payload, saisieMode) === 'cahier',
            { justifier: libre }
        );
    }
    const key = libre ? 'saisie' : consigneKeyFromPayload(payload);
    if (!key) return '';
    if (modeEffectif(payload, saisieMode) === 'cahier') {
        return CONSIGNES_CAHIER[key] || CONSIGNES[key] || '';
    }
    return CONSIGNES[key] || '';
};
